import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { getInsurancePrice } from '../../../api/endpoints/insurance';
import type { TamamliyoPriceData } from '../../../api/types/insurance';
import type { ShoppingCart } from '../../../api/types/shoppingCart';
import { isFeatureEnabled } from '../../../config/featureFlags';
import { buildInsuranceRequestsFromCart } from '../insurance';

/** `urunBilgileri.teminatlar` sağlayıcıdan `string[]`, `{ tr: {...} }` çok dilli sözlük ya
 *  da düz sözlük gelebilir (legacy `additionalservices.js` `.tr` anahtarlarını iteriyor). */
function readTeminatlar(priceData: TamamliyoPriceData | undefined): string[] {
  const raw = (priceData?.urunBilgileri as { teminatlar?: unknown } | undefined)?.teminatlar;
  if (Array.isArray(raw)) return raw.filter((x): x is string => typeof x === 'string');
  if (raw && typeof raw === 'object') {
    const dict = (raw as Record<string, unknown>).tr ?? raw;
    if (dict && typeof dict === 'object') return Object.keys(dict as Record<string, unknown>);
  }
  return [];
}

/**
 * Uçuş ödeme adımında sigorta teklifi — legacy `Views/Flight/Partials/_AdditionalServices.cshtml`
 * + `additionalservices.js`'in React karşılığı. `Insurance/GetPrice` ile fiyat + teminatları
 * gösterir, "İstemiyorum / İstiyorum" radiosu ile seçimi `onChange` üzerinden flow state'e
 * yazdırır. Poliçe kesme `runPayment`'te (uçuş biletlendikten sonra). Fiyat alınamazsa panel
 * bir uyarı gösterir ama akışı BLOKE ETMEZ.
 */
export function AirInsurancePanel({
  cart,
  wants,
  onChange,
}: {
  cart: ShoppingCart | null | undefined;
  wants: boolean;
  onChange: (wants: boolean) => void;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const requests = useMemo(() => buildInsuranceRequestsFromCart(cart), [cart]);

  const priceQuery = useQuery({
    queryKey: ['airInsurancePrice', cart?.id],
    queryFn: () => getInsurancePrice(requests!.getPrice),
    enabled: isFeatureEnabled('HasInsurance') && !!requests && !!cart?.id,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (!isFeatureEnabled('HasInsurance') || !requests) return null;

  const priceData = priceQuery.data?.data;
  const urun = priceData?.urunBilgileri;
  const sirket = priceData?.sigortaSirketiBilgileri;
  const teminatlar = readTeminatlar(priceData);
  const priceText = urun?.fiyat;
  const failed = priceQuery.isError || (priceQuery.isSuccess && !priceData);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">Seyahat Sağlık Sigortası (Tamamlıyo)</Typography>
        {priceText && (
          <Typography variant="subtitle1" color="primary">
            + {priceText}
          </Typography>
        )}
      </Stack>

      {priceQuery.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {failed && (
        <Alert severity="warning">Sigorta teklifi alınamadı — uçuşla devam edebilirsiniz.</Alert>
      )}

      {!priceQuery.isLoading && !failed && priceData && (
        <>
          {(urun?.urunAdi || urun?.urunTanimi) && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {urun?.urunAdi}
              {urun?.urunTanimi ? ` — ${urun.urunTanimi}` : ''}
            </Typography>
          )}

          <RadioGroup
            row
            value={wants ? 'true' : 'false'}
            onChange={(e) => onChange(e.target.value === 'true')}
            sx={{ alignItems: 'center', gap: 1 }}
          >
            <FormControlLabel value="false" control={<Radio size="small" />} label="İstemiyorum" />
            <FormControlLabel value="true" control={<Radio size="small" />} label="İstiyorum" />
            <Chip
              size="small"
              color={wants ? 'success' : 'primary'}
              label={wants ? 'Hizmeti Eklediniz' : 'Önerilir'}
            />
          </RadioGroup>

          {teminatlar.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
              {teminatlar.slice(0, 4).map((t, i) => (
                <Chip key={i} size="small" variant="outlined" label={t} />
              ))}
              {teminatlar.length > 4 && (
                <Button size="small" onClick={() => setDetailsOpen(true)}>
                  Detayları Göster
                </Button>
              )}
            </Stack>
          )}

          {(sirket?.tamAdi || sirket?.yayinci) && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              {sirket?.yayinci ?? sirket?.tamAdi}
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
            Poliçe ücreti uçuş ödemesinden ayrı olarak tahsil edilir. 3D Secure uçuş ödemesinde
            sigorta, bankadan dönüşte onay sayfasında tamamlanır.
          </Typography>
        </>
      )}

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Tüm Teminatlar</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ pt: 1 }}>
            {teminatlar.map((t, i) => (
              <Typography key={i} variant="body2">
                • {t}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </Paper>
  );
}
